<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
import { computed, onMounted, reactive, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const DEFAULT_CONFIG = {
  enabled: false,
  inbox_ids: [],
  delay_seconds: 8,
  analysis_scope: 'all',
  minimum_score: 18,
  stop_after_human_reply: true,
  reply_once_per_conversation: false,
  fallback_enabled: false,
  fallback_zh: '',
  fallback_en: '',
  fallback_hi: '',
};

const store = useStore();
const { currentAccount } = useAccount();
const form = reactive({ ...DEFAULT_CONFIG });

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const isFetchingInboxes = computed(
  () => store.getters['inboxes/getUIFlags']?.isFetching
);
const isUpdating = computed(
  () => store.getters['accounts/getUIFlags']?.isUpdating
);

const allInboxIds = computed(() =>
  inboxes.value.map(inbox => Number(inbox.id)).filter(Boolean)
);

const areAllInboxesSelected = computed(() => {
  if (!allInboxIds.value.length) return false;
  return allInboxIds.value.every(id => form.inbox_ids.includes(id));
});

const isAllInboxSelection = computed(
  () => !form.inbox_ids.length || areAllInboxesSelected.value
);

const selectedInboxNames = computed(() => {
  const ids = form.inbox_ids.map(Number);
  if (isAllInboxSelection.value) return '全部收件箱';
  const names = inboxes.value
    .filter(inbox => ids.includes(Number(inbox.id)))
    .map(inbox => inbox.name);
  return names.length ? names.join('、') : '未选择收件箱';
});

const previewText = computed(() => {
  if (!form.enabled) return '本地机器人未启用，不会自动回复任何玩家消息。';
  const scopeText =
    form.analysis_scope === 'all'
      ? '分析该会话全部玩家消息'
      : '只分析最后一条玩家消息';
  return `启用后，系统会延迟 ${form.delay_seconds} 秒，${scopeText}，从客服话术中匹配分数不低于 ${form.minimum_score} 的回复，并按玩家语言自动发送。`;
});

const normalizeConfig = config => ({
  ...DEFAULT_CONFIG,
  ...(config || {}),
  enabled: Boolean(config?.enabled),
  inbox_ids: Array(config?.inbox_ids || [])
    .map(Number)
    .filter(Boolean),
  delay_seconds: Number(config?.delay_seconds || DEFAULT_CONFIG.delay_seconds),
  minimum_score: Number(config?.minimum_score || DEFAULT_CONFIG.minimum_score),
  stop_after_human_reply:
    config?.stop_after_human_reply ?? DEFAULT_CONFIG.stop_after_human_reply,
  reply_once_per_conversation: Boolean(config?.reply_once_per_conversation),
  fallback_enabled: Boolean(config?.fallback_enabled),
});

const applyAccountConfig = () => {
  Object.assign(
    form,
    normalizeConfig(currentAccount.value?.settings?.kefubao_local_bot)
  );
};

const toggleInbox = inboxId => {
  const id = Number(inboxId);
  if (!form.inbox_ids.length) form.inbox_ids = [...allInboxIds.value];
  const index = form.inbox_ids.indexOf(id);
  if (index >= 0) form.inbox_ids.splice(index, 1);
  else form.inbox_ids.push(id);
};

const selectAllInboxes = () => {
  form.inbox_ids = [...allInboxIds.value];
};

const saveConfig = async () => {
  const delay = Math.min(Math.max(Number(form.delay_seconds) || 8, 1), 60);
  const minimumScore = Math.min(
    Math.max(Number(form.minimum_score) || 18, 1),
    200
  );

  try {
    await store.dispatch('accounts/update', {
      kefubao_local_bot: {
        enabled: form.enabled,
        inbox_ids: form.inbox_ids.map(Number).filter(Boolean),
        delay_seconds: delay,
        analysis_scope: form.analysis_scope,
        minimum_score: minimumScore,
        stop_after_human_reply: form.stop_after_human_reply,
        reply_once_per_conversation: form.reply_once_per_conversation,
        fallback_enabled: form.fallback_enabled,
        fallback_zh: form.fallback_zh,
        fallback_en: form.fallback_en,
        fallback_hi: form.fallback_hi,
      },
    });
    form.delay_seconds = delay;
    form.minimum_score = minimumScore;
    useAlert('本地机器人配置已保存');
  } catch (error) {
    useAlert('保存失败，请稍后重试');
  }
};

watch(
  () => currentAccount.value?.settings?.kefubao_local_bot,
  applyAccountConfig,
  { immediate: true }
);

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout :is-loading="isFetchingInboxes" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        title="本地机器人"
        description="基于客服话术库自动回复玩家消息，不依赖外部机器人服务。机器人会识别玩家语言，并选择中文、英语或印地语回复。"
      />
    </template>

    <template #body>
      <div class="max-w-6xl">
        <div
          class="mb-5 flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 shadow-sm"
        >
          <div class="flex min-w-0 items-center gap-3">
            <span
              class="inline-flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-bot" class="size-5" />
            </span>
            <div>
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                本地规则自动回复
              </h2>
              <p class="m-0 mt-0.5 text-sm text-n-slate-11">
                读取玩家消息上下文，匹配客服话术后自动发送。
              </p>
            </div>
          </div>
          <label
            class="relative inline-flex cursor-pointer items-center gap-3 rounded-full border border-n-weak bg-n-background py-2 pl-4 pr-2 text-sm font-medium text-n-slate-12"
          >
            <span>{{ form.enabled ? '已启用' : '未启用' }}</span>
            <input
              v-model="form.enabled"
              class="peer sr-only"
              type="checkbox"
            />
            <span
              class="h-6 w-11 rounded-full bg-n-slate-6 transition peer-checked:bg-n-brand"
            />
            <span
              class="absolute right-7 size-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
            />
          </label>
        </div>

        <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <section
            class="overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-sm"
          >
            <div class="border-b border-n-weak bg-n-solid-1 px-5 py-4">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                回复规则
              </h3>
              <p class="m-0 mt-1 text-sm leading-6 text-n-slate-11">
                建议保持“客服手动回复后停止接管”开启，避免机器人覆盖人工处理。
              </p>
            </div>

            <div class="space-y-5 p-5">
              <div class="grid gap-4 md:grid-cols-3">
                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    触发延迟
                  </span>
                  <div class="relative">
                    <input
                      v-model.number="form.delay_seconds"
                      class="h-11 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                      max="60"
                      min="1"
                      type="number"
                    />
                    <span
                      class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-n-slate-10"
                    >
                      秒
                    </span>
                  </div>
                </label>

                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    分析范围
                  </span>
                  <select
                    v-model="form.analysis_scope"
                    class="h-11 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option value="all">分析全部玩家消息</option>
                    <option value="latest">只看最后一条</option>
                  </select>
                </label>

                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    最低匹配分
                  </span>
                  <input
                    v-model.number="form.minimum_score"
                    class="h-11 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                    max="200"
                    min="1"
                    type="number"
                  />
                </label>
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <label
                  class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <input
                    v-model="form.stop_after_human_reply"
                    class="mt-1"
                    type="checkbox"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      客服手动回复后停止接管
                    </span>
                    <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                      同一会话只要客服回复过，机器人不再自动回复。
                    </span>
                  </span>
                </label>

                <label
                  class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <input
                    v-model="form.reply_once_per_conversation"
                    class="mt-1"
                    type="checkbox"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      每个会话最多自动回复一次
                    </span>
                    <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                      适合欢迎语或首轮处理，避免重复触发。
                    </span>
                  </span>
                </label>
              </div>

              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                      作用收件箱
                    </h3>
                    <p class="m-0 mt-1 text-sm text-n-slate-11">
                      当前：{{ selectedInboxNames }}
                    </p>
                  </div>
                  <button
                    class="h-8 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12 hover:bg-n-alpha-black2"
                    type="button"
                    @click="selectAllInboxes"
                  >
                    全部收件箱
                  </button>
                </div>

                <div class="grid gap-2 md:grid-cols-2">
                  <label
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 hover:border-n-brand"
                  >
                    <input
                      :checked="
                        isAllInboxSelection ||
                        form.inbox_ids.includes(Number(inbox.id))
                      "
                      type="checkbox"
                      @change="toggleInbox(inbox.id)"
                    />
                    <span class="min-w-0 truncate">{{ inbox.name }}</span>
                  </label>
                </div>
              </div>

              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <label class="mb-3 flex items-start gap-3">
                  <input
                    v-model="form.fallback_enabled"
                    class="mt-1"
                    type="checkbox"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      没有匹配话术时发送兜底回复
                    </span>
                    <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                      关闭后，没有匹配到客服话术时机器人不会回复。
                    </span>
                  </span>
                </label>

                <div class="grid gap-3 md:grid-cols-3">
                  <label class="grid gap-2">
                    <span class="text-sm font-medium text-n-slate-12">
                      中文兜底
                    </span>
                    <textarea
                      v-model="form.fallback_zh"
                      class="min-h-24 rounded-lg border border-n-weak bg-n-background p-3 text-sm outline-none focus:border-n-brand"
                    />
                  </label>
                  <label class="grid gap-2">
                    <span class="text-sm font-medium text-n-slate-12">
                      英语兜底
                    </span>
                    <textarea
                      v-model="form.fallback_en"
                      class="min-h-24 rounded-lg border border-n-weak bg-n-background p-3 text-sm outline-none focus:border-n-brand"
                    />
                  </label>
                  <label class="grid gap-2">
                    <span class="text-sm font-medium text-n-slate-12">
                      印地语兜底
                    </span>
                    <textarea
                      v-model="form.fallback_hi"
                      class="min-h-24 rounded-lg border border-n-weak bg-n-background p-3 text-sm outline-none focus:border-n-brand"
                    />
                  </label>
                </div>
              </div>

              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <div class="mb-2 flex items-center gap-2">
                  <Icon icon="i-lucide-info" class="size-4 text-n-brand" />
                  <p class="m-0 text-sm font-semibold text-n-slate-12">
                    当前规则预览
                  </p>
                </div>
                <p class="m-0 text-sm leading-6 text-n-slate-11">
                  {{ previewText }}
                </p>
              </div>

              <div
                class="flex items-center justify-end gap-3 border-t border-n-weak pt-4"
              >
                <button
                  class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 transition hover:bg-n-alpha-black2"
                  type="button"
                  @click="applyAccountConfig"
                >
                  取消修改
                </button>
                <button
                  class="h-9 rounded-lg bg-n-brand px-4 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isUpdating"
                  type="button"
                  @click="saveConfig"
                >
                  {{ isUpdating ? '保存中...' : '保存配置' }}
                </button>
              </div>
            </div>
          </section>

          <aside
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          >
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">使用说明</h3>
            <div class="mt-3 space-y-3 text-sm leading-6 text-n-slate-11">
              <p class="m-0">
                机器人只会回复玩家发来的公开消息，不会处理私密便签或系统消息。
              </p>
              <p class="m-0">
                用户连续发送多条消息时，旧任务会自动失效，最后一条消息触发的任务会分析完整上下文。
              </p>
              <p class="m-0">
                回复语言根据玩家消息自动识别：印地语优先，其次中文，否则使用英语。
              </p>
              <p class="m-0">
                推荐先在测试收件箱启用，确认话术匹配效果后再扩大范围。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
